import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BankAccount } from '@features/bankAccounts/types/bankAccount'
import { defaultBankTransactionsQuery } from '@features/bankTransactions/hooks/useBankTransactionsFilters'
import { BankTransactionsFilters } from '@features/bankTransactions/types/bankTransactionsFilters'
import { ReconcilableResponse } from '@features/paymentReconciliationModal/query-api'
import { BookkeepingItem } from '@features/reconciliation/types/bookkeepingItem'
import { Account } from '@modules-deprecated/app/accounts/types'
import { BankLine, BankLineMatch, ReconciliationSuggestion } from '@modules-deprecated/bankReconciliation/types'

import { sandboxReducer } from './sandbox/sandboxReducer'
import { buildSandboxSeedData, SANDBOX_BOOKKEEPING_ACCOUNT_ID } from './sandbox/sandboxSeedData'

export interface SandboxState {
  accounts: Account[]
  bankAccounts: BankAccount[]
  bankLines: BankLine[]
  bookkeepingItems: BookkeepingItem[]
  suggestions: ReconciliationSuggestion[]
  matchedItemsByMatchId: Record<string, BookkeepingItem[]>
  currency: string
}

interface SandboxModeContextValue {
  sandboxState: SandboxState
  filters: BankTransactionsFilters
  setFilters: (
    params: Partial<BankTransactionsFilters> | ((prev: BankTransactionsFilters) => Partial<BankTransactionsFilters>),
  ) => void
  matchBankLineInSandbox: (bankLineId: string, bookkeepingItems: BookkeepingItem[]) => ReconcilableResponse
  unmatchBankLineInSandbox: (matchId: string) => void
  dismissSuggestionInSandbox: (bankLineId: string) => void
}

interface SandboxModeProviderProps {
  children: ReactNode
}

const SandboxModeContext = createContext<SandboxModeContextValue | undefined>(undefined)

export const SandboxModeProvider = ({ children }: SandboxModeProviderProps): ReactElement => {
  const { i18n, t } = useTranslation()
  const [sandboxState, dispatch] = useReducer(sandboxReducer, undefined, () => buildSandboxSeedData(i18n.language, t))

  // Sandbox-local filter state, shared across every useBankTransactionsFilters() call site instead
  // of the URL query string the real route uses — the sandbox pane overlays /bank-accounts, so
  // writing bank-transaction filter params onto that URL would be wrong, and this state also has to
  // live here (not in a per-instance hook) so remounting this provider on reset clears it for free.
  // accountId is seeded upfront (unlike the real route, which starts empty and backfills it once
  // useDefaultBookkeepingBankAccount resolves) because the sandbox account is a fixed constant, not
  // async data — leaving it empty for even one render made the account balance and list flash away
  // and back on every mount (initial open and reset alike).
  const [filters, setFiltersState] = useState<BankTransactionsFilters>({
    ...defaultBankTransactionsQuery,
    accountId: SANDBOX_BOOKKEEPING_ACCOUNT_ID,
  })

  const setFilters = useCallback(
    (
      params: Partial<BankTransactionsFilters> | ((prev: BankTransactionsFilters) => Partial<BankTransactionsFilters>),
    ) => {
      setFiltersState((prev) => ({ ...prev, ...(typeof params === 'function' ? params(prev) : params) }))
    },
    [],
  )

  const matchBankLineInSandbox = useCallback(
    (bankLineId: string, bookkeepingItems: BookkeepingItem[]): ReconcilableResponse => {
      const bankLine = sandboxState.bankLines.find((line) => line.id === bankLineId)

      if (!bankLine) {
        return {}
      }

      const match: BankLineMatch = {
        id: `sandbox-match-${bankLineId}`,
        accountId: bankLine.accountId,
        entryDate: bankLine.entryDate,
        amount: bankLine.amount,
        side: bankLine.side,
        feeAccountId: null,
        isApproved: true,
        approvedTime: new Date().toISOString(),
        lines: [bankLine],
        lineIds: [bankLine.id],
        subjectAssociationIds: [],
        description: bankLine.description,
        currencyId: sandboxState.currency,
      }

      dispatch({ type: 'MATCH_BANK_LINE', bankLineId, match, bookkeepingItems })

      return { bankLines: [{ ...bankLine, matchId: match.id, match }], bankLineMatches: [match] }
    },
    [sandboxState.bankLines, sandboxState.currency],
  )

  const unmatchBankLineInSandbox = useCallback((matchId: string) => {
    dispatch({ type: 'UNMATCH_BANK_LINE', matchId })
  }, [])

  const dismissSuggestionInSandbox = useCallback((bankLineId: string) => {
    dispatch({ type: 'DISMISS_SUGGESTION', bankLineId })
  }, [])

  const value = useMemo(
    () => ({
      sandboxState,
      filters,
      setFilters,
      matchBankLineInSandbox,
      unmatchBankLineInSandbox,
      dismissSuggestionInSandbox,
    }),
    [sandboxState, filters, setFilters, matchBankLineInSandbox, unmatchBankLineInSandbox, dismissSuggestionInSandbox],
  )

  return <SandboxModeContext.Provider value={value}>{children}</SandboxModeContext.Provider>
}

export const useSandboxModeContext = (): SandboxModeContextValue | undefined => useContext(SandboxModeContext)
